/**
 * Rewards Grid Service - Get all required data
 *
 * @memberof RewardGrid
 */

import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {RewardHistoryModel, RewardModel} from './data.model';
import {ParamService} from './param.service';
import {v4 as uuidv4} from 'uuid';
import {environment} from './../../../environments/environment';
import {properties} from './../../../environments/properties';
import {
    CommonService
} from 'abs-ngx-library/src/lib/core';

// Data Model

@Injectable({
    providedIn: 'root',
})
export class RewardService {
    banner = '';

    public constructor(private _http: HttpClient, private paramService: ParamService, private _commonService: CommonService){
        this.banner = this.paramService.banner
    }

    getLocationHref() {
        return window.location.href;
    }

    getLocationHost() {
        return window.location.host;
    }

    getApplicationDomainURL() {
        let domain;
        if (this.getLocationHref().indexOf('local') > 0) {
            domain = 'https://www-qa1.' + this.banner + '.com';
        } else {
            domain = 'https://' + this.getLocationHost();
        }
        return domain;
    }

    public getMobileApplicationDomainURL(currentEnvironment, banner) {
        let domain = '';
        if (currentEnvironment.indexOf('prod') >= 0) {
            domain = 'https://www.' + banner + '.com';
        } else {
            domain = 'https://www-' + currentEnvironment + '.' + banner + '.com';
        }
        return domain;
    }

    getEnvironment() {
        let appEnvironment;
        if (window.location.origin.indexOf('qa1') > 0 || window.location.origin.indexOf('qa2') > 0 || window.location.origin.indexOf('qa3') > 0 || window.location.origin.indexOf('stage') > 0) {
            appEnvironment = window.location.host.split('-')[1].split('.')[0];
        } else if (window.location.origin.indexOf('local') > 0) {
            appEnvironment = 'qa1';
        } else {
            appEnvironment = 'prod';
        }
        return appEnvironment;
    }

    public replaceHeaderBanner(header) {
        if (header) {
            if (header['banner']) {
                header['banner'] = header['banner'].replace(/\{banner\}/g, this.banner);
            }
            if (header['x-swy_banner']) {
                header['x-swy_banner'] = header['x-swy_banner'].replace(/\{banner\}/g, this.banner);
            }
            if (header['X-SWY_BANNER']) {
                header['X-SWY_BANNER'] = header['X-SWY_BANNER'].replace(/\{banner\}/g, this.banner);
            }

        }
    }

    parseJwt(jwtToken) {
        const base64Url = jwtToken.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    getUserClaims(user) {
        const oktaToken = this.parseJwt(user.SWY_SHOP_TOKEN);
        const userClaims = {
            aln: oktaToken['aln'],
            hid: oktaToken['hid'],
            zip: oktaToken['zip'],
            gid: oktaToken['gid'],
            uuid: oktaToken['uuid'],
            exp: oktaToken['exp'],
            firstname: oktaToken['fnm'],
            lastname: oktaToken['lnm']
        };
        return userClaims;
    }

    public getData(): Observable<any> {
        const rewardGridOffers = this.get('abRewards');
        let result = null;
        result = this.getRemoteData();
        return result;
    }

    public getDataMulticlip(userInfo, currentEnv, enableCashRewards): Observable<any> {
        let result = of('');
        result = this.getRemoteDataMulticlip(userInfo, currentEnv, enableCashRewards);
        return result;
    }

    public getRemoteData(): Observable<any> {
        const dataUrl = window['AB'].rewardPath + '?storeId=' + window['AB'].userInfo.j4u.storeId + '&sortAsc=specialRank';
        const httpOptions = {headers: new HttpHeaders(window['AB'].j4uHttpOptions)};

        return this._http.get(dataUrl, httpOptions).pipe(map(response => {
            if (response && response['grOffers']) {
                let savedTime: any = new Date();
                savedTime = Date.parse(savedTime);
                const savingData = {
                    savedTime,
                    offers: response['grOffers'],
                    storeId: window['AB'].userInfo.j4u.storeId
                };
                this.set('abRewards', savingData);
                return response['grOffers'];
            } else {
                return '';
            }
        }));
    }

    public getRemoteDataMulticlip(userInfo, currentEnvironment, enableCashRewards): Observable<any> {
        let params: any;
        let httpOptions: any;
        params = {
            rewardsSimplified: enableCashRewards
        };
        let storeId;
        storeId = this.getStoreIdForUserType(userInfo)
        const dataUrl = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.getRewardsOffers + '?storeId=' + storeId + '&sortAsc=specialRank';
        const headerObj = environment[currentEnvironment].rewardsLookupConfig.j4uHttpOptions30Header;
        this.replaceHeaderBanner(headerObj);
        if (enableCashRewards) {
            httpOptions = {
                headers: {
                    ...headerObj,
                    'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                    'x-swy-correlation-id': uuidv4()
                },
                params: params
            };
        } else {
            httpOptions = {
                headers: {
                    ...headerObj,
                    'X-swyConsumerDirectoryPro': userInfo.SWY_SHOP_TOKEN,
                    'SWY_SSO_TOKEN': userInfo.SWY_SHOP_TOKEN,
                    'x-swy-correlation-id': uuidv4()
                }
            };
        }
        return this._http.get(dataUrl, httpOptions).pipe(
            map(response => {
                if (response && response['grOffers']) {
                    let savedTime: any = new Date();
                    savedTime = Date.parse(savedTime);

                    const savingData = {
                        savedTime,
                        offers: response['grOffers'].filter(a => a.programType !== 'Auto Reward' && a.programType !== 'Points for Services'),
                        cashOffer: response['grOffers'].filter(a => a.programType === 'Auto Reward'),
                        moreWaysToUseOffers: response['grOffers'].filter(a => a.programType === 'Points for Services'),
                        storeId: window['AB'].userInfo.j4u.storeId
                    };
                    this.set('abRewards', savingData);
                    return savingData;
                } else {
                    return '';
                }
            })
        );
    }

    private set(key: string, data: any): void {
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (e) {
            console.error('Error saving to localStorage', e);
        }
    }

    private get(key: string) {
        try {
            return JSON.parse(localStorage.getItem(key));
        } catch (e) {
            console.error('Error getting data from localStorage', e);
            return null;
        }
    }

    private remove(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.error('Error removing data from localStorage', e);
        }
    }

    // For reward details call + mapping of data
    public getOneReward(offerId, userInfo, currentEnvironment): Observable<RewardModel> {
        let httpOptions: any;
        const headerObj = environment[currentEnvironment].rewardsLookupConfig.j4uHttpOptions30Header;
        this.replaceHeaderBanner(headerObj);
        httpOptions = {
            headers: {
                ...headerObj,
                'X-swyConsumerDirectoryPro': userInfo.SWY_SHOP_TOKEN,
                'SWY_SSO_TOKEN': userInfo.SWY_SHOP_TOKEN,
                'x-swy-correlation-id': uuidv4()
            }
        };
        const dataUrl = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.offerDetailPath + '/' + offerId;
        return this._http.get(dataUrl, httpOptions).pipe(
            map(data => {
                const reward: RewardModel = new RewardModel;
                if (data) {
                    reward['endDate'] = data['offerEndDate'];
                    reward['imageId'] = data['imageId'];
                    reward['imagePath'] = window['SWY'].CONFIGSERVICE.nimbusConfig.couponImageBaseUrl + data['imageId'];
                    reward['offerType'] = window['SWY'].CONFIGSERVICE.nimbusConfig.offerTypes[data['usageType']];
                    reward['generalDisclaimer'] = (data['offerDetail']['disclaimer']) ? data['offerDetail']['disclaimer'] : '';
                    reward['offerPgm'] = data['offerPgm'];
                    reward['offerSubPgm'] = data['offerSubPgm'];
                    reward['offerPrice'] = data['offerDetail'].savingsValue;
                    reward['title'] = data['offerDetail'].titleDsc1 + ((data['offerDetail'].titleDsc2) ? (' ' + data['offerDetail'].titleDsc2) : ' ');
                    reward['description'] = data['offerDetail'].prodDsc1;
                    reward['rewardsRefundable'] = data['rewardsRefundable'];
                    reward['clipId'] = data['clipId'];
                    reward['pointsRequired'] = data['pointsRequired'];
                }
                return reward;
            })
        );
    }

    // THIS IS FOR REWARD DETAIL PAGE
    public getOneRewardMulticlip(offerId, offerType, currentEnvironment, userInfo): Observable<RewardModel> {
        let httpOptions: any;
        const headerObj = environment[currentEnvironment].rewardsLookupConfig.offerDetailsXAPIHeader;
        this.replaceHeaderBanner(headerObj);
        httpOptions = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        let dataUrl = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.offerDetailxAPIPath;
        // const storeId = window['AB'].userInfo.branchId;
        let storeId;
        storeId = this.getStoreIdForUserType(userInfo);
        dataUrl = dataUrl + '?offerId=' + offerId + '&storeId=' + storeId + '&offerPgm=' + offerType + '&includeUpc=y';
        return this._http.get(dataUrl, httpOptions).pipe(map(data => {
            const reward: RewardModel = new RewardModel;
            if (data) {
                const offerPgmLabel = window['SWY'].CONFIGSERVICE.nimbusConfig.couponTypes[data['offerPgm']];
                reward['endDate'] = data['offerEndDate'];
                reward['imageId'] = data['imageId'];
                reward['imagePath'] = data['imageId'];
                reward['offerType'] = data['usageType'];
                reward['usageType'] = data['usageType'];
                reward['generalDisclaimer'] = (data['offerDetail'].details) ? data['offerDetail'].details : '';
                reward['offerPgm'] = data['offerPgm'];
                reward['offerPrice'] = data['offerDetail'].offerPrice;
                reward['title'] = data['offerDetail'].name;
                reward['description'] = data['offerDetail'].description;
                reward['rewardsRefundable'] = data['offerDetail'].rewardsRefundable;
                reward['clipDetails'] = (data['clipDetails'] ? data['clipDetails'] : '');
                reward['status'] = data['status'];
                reward['clipId'] = data['clipId'];
                reward['displayEndDate'] = data['displayEndDate'];
                reward['pointsRequired'] = data['pointsRequired'];
                reward['upcList'] = data['upcList'];
            }
            return reward;
        }));
    }

    public updateTimeToRefresh() {
        const rewardGridOffers = this.get('abRewards');

        if (rewardGridOffers) {
            rewardGridOffers.savedTime = '';
            this.set('abRewards', rewardGridOffers);
        }
    }

    public dateConversion(dateServer: string): string {
        const dateStr = dateServer;
        const regex = /\d+/;
        const mills = regex.exec(dateStr);
        let result = '';
        if (Array.isArray(mills)) {
            const d = new Date(Number(mills[0]));
            d.setUTCMilliseconds(mills.index);
            result = d.toLocaleDateString('en-US');
        }

        return result;
    }

    public getRewardHistory(userInfo: any, currentEnvironment: any): Observable<RewardHistoryModel> {

        let params: any;

        params = {
            numberOfDays: '90'
        };

        if (window['AB'].COMMON.getURLParameterByName('reload') === 'true') {
            const dateStamp = new Date();
            params.rand = dateStamp.valueOf();
        }
        const headerOBJ = environment[currentEnvironment].rewardsLookupConfig.xAPIOcrpRewardsHttpOptions;
        this.replaceHeaderBanner(headerOBJ);
        const options = {
            headers: {
                ...headerOBJ,
                'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                'x-swy-correlation-id': uuidv4()
            },
            params: params
        };
        let rewardsHistoryURL = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.getRewardsHistory;
        const userClaims = this.getUserClaims(userInfo);
        rewardsHistoryURL = rewardsHistoryURL  + '/' + userClaims.hid;
        return this._http.get(rewardsHistoryURL, options).pipe(map(data => {

            const rewardHistory = new RewardHistoryModel;
            if (data) {
                rewardHistory.hhid = data['hhid'];
                try {
                    rewardHistory.transactionHistories = JSON.parse(JSON.stringify(data['transactionHistories']));
                } catch (e) {
                    rewardHistory.transactionHistories = [];
                }
            }
            return rewardHistory;
        }));
    }

    public getRewardHistoryV2(pageNum, currentEnvironment, userInfo): Observable<RewardHistoryModel> {
        let params: any;

        params = {
            numberOfDays: 90,
            pageNo: pageNum,
            pageSize: 10,
        };

        if (window['AB'].COMMON.getURLParameterByName('reload') === 'true') {
            const dateStamp = new Date();
            params.rand = dateStamp.valueOf();
        }

        const headerOBJ = environment[currentEnvironment].rewardsLookupConfig.xAPIOcrpRewardsHttpOptionsV2;
        this.replaceHeaderBanner(headerOBJ);
        const options = {
            headers: {
                ...headerOBJ,
                'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                'x-swy-correlation-id': uuidv4()
            },
            params: params
        };
        let rewardsHistoryUrlV2 = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.getRewardHistoryV2Path;
        const userClaims = this.getUserClaims(userInfo);
        rewardsHistoryUrlV2 = rewardsHistoryUrlV2 + '/' + userClaims.hid;
        return this._http.get(rewardsHistoryUrlV2, options).pipe(map(data => {
            const rewardHistory = new RewardHistoryModel;
            if (data) {
                rewardHistory.hhid = data['hhid'];
                try {
                    rewardHistory.transactionHistories = JSON.parse(JSON.stringify(data['transactionHistories']));
                } catch (e) {
                    rewardHistory.transactionHistories = [];
                }
                try {
                    rewardHistory.pageable = JSON.parse(JSON.stringify(data['pageable']));
                } catch (e) {
                    rewardHistory.pageable = null;
                }
            }
            return rewardHistory;
        }));
    }

    public getPointsHistory(pageNum, currentEnvironment, userInfo): Observable<RewardHistoryModel> {
        let params: any;

        params = {
            numberOfDays: 90,
            pageNo: pageNum,
            pageSize: 10,
        };

        if (window['AB'].COMMON.getURLParameterByName('reload') === 'true') {
            const dateStamp = new Date();
            params.rand = dateStamp.valueOf();
        }

        const headerOBJ = environment[currentEnvironment].rewardsLookupConfig.xAPIOcrpRewardsHttpOptionsV3;
        this.replaceHeaderBanner(headerOBJ);
        const options = {
            headers: {
                ...headerOBJ,
                'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                'x-swy-correlation-id': uuidv4(),
                'X-ABS-Client-ID': 'WEB'
            },
            params: params
        };
        let rewardsHistoryUrlV3 = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.getRewardHistoryV3Path;
        const userClaims = this.getUserClaims(userInfo);
        rewardsHistoryUrlV3 = rewardsHistoryUrlV3 + '/' + userClaims.hid;
        return this._http.get(rewardsHistoryUrlV3, options).pipe(map(data => {
            const rewardHistory = new RewardHistoryModel;
            if (data) {
                rewardHistory.hhid = data['hhid'];
                try {
                    rewardHistory.transactionHistories = JSON.parse(JSON.stringify(data['transactionHistories']));
                } catch (e) {
                    rewardHistory.transactionHistories = [];
                }
                try {
                    rewardHistory.pageable = JSON.parse(JSON.stringify(data['pageable']));
                } catch (e) {
                    rewardHistory.pageable = null;
                }
            }
            return rewardHistory;
        }));
    }

    getStoreIdForUserType(userInfo) {
        if (userInfo && userInfo.storeId) {
            return userInfo.storeId;
        } else if(userInfo && userInfo.shopStoreId)  {
            return userInfo.shopStoreId;
        } else {
            return  userInfo.j4uStoreId
        }
    }

    getPromotions(currentEnvironment, userInfo, banner) {
        let promotionsUrl = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.loyaltyPromotions;
        const storeId = userInfo.j4uStoreId ? userInfo.j4uStoreId : userInfo.storeId;
        promotionsUrl = promotionsUrl + '?date=current&includedBanners=' + banner + '&userTypes=all&storeId=' + storeId;
        return this._http.get(promotionsUrl).pipe(map(data => {
            return data;
        }));
    }

    getPromotionsV2(currentEnvironment, userInfo, pageSource){
        let httpOptions: any;
        const headerObj = environment[currentEnvironment].rewardsLookupConfig.partnerPromotionV2HttpOptions;
        this.replaceHeaderBanner(headerObj);
        if(userInfo.SWY_SHOP_TOKEN){
            httpOptions = {
                headers: {
                    ...headerObj,
                    'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`
                }
            }
        }else{
            httpOptions = {
                headers: {
                    ...headerObj,
                    'Authorization': ''
                }
            }
        }
        let promotionsUrl = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.loyaltyPromotionsV2;
        const storeId = userInfo.j4uStoreId ? userInfo.j4uStoreId : userInfo.storeId;
        promotionsUrl = promotionsUrl + '?storeId='+ storeId +'&pageSource=' + pageSource;
        return this._http.get(promotionsUrl, httpOptions).pipe(map(data => {
            return data;
        }));
    }

    getDynamicPartnerPromoCodeLink(promotionCodeLinkUrl, currentEnvironment,partnerOfferId,userInfo, channel) {
        let httpOptions: any;
        const body = {partnerOfferId: partnerOfferId};
        const headerObj = environment[currentEnvironment].rewardsLookupConfig.dynamicPartnerPromoCodeLinkHttpOptions;
        this.replaceHeaderBanner(headerObj);
        httpOptions = {
            headers: {
                ...headerObj,
                'Channel': channel,
                'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`
            }
        }
        
        return this._http.post(promotionCodeLinkUrl, body, httpOptions).pipe(map(data => {
            return data;
        }));
    }

    getMockPromotionData (currentEnvironment, userInfo, banner) {
        const successRespData = {"offers":[{"partnerOfferId":"CRUNCHFITNESS-1","partnerName":"crunchfit","isStatic":true,"staticPromo":"https://www.crunch.com/loyalty","partnerExternalUrl":"https://www.crunch.com/","title":"Crunchfitness","subTitle":"Crunchfitness only for All customers","contentPath":"/content/experience-fragments/xf/contenthub/external-partner-offer/crunchfitness/offer-detail/crunch-offer-1","bgImgPath":"https://images.albertsons-media.com/is/image/ABSStage/doordash-test-banner","bgImgAltText":"CrunchFitness","startDate":"2024-12-01 08:29:00.0","endDate":"2025-03-26 08:29:00.0","displayOrder":"1","partnerProgramType":"all","paidSubscriptionSegment":[],"storeSegment":["division-norcal"],"ctaText":"Claim Offer","ctaAltText":"Claim offer","analyticsTag":"Crunch Fitness Internal Page","ctaLandingPgTitleMobile":"Crunch Fitness","ctaTextMobile":"Claim offer","ctaAltTextMobile":"Click to save now for Crunch Fitness Promotions","storeList":["1087","1108","1109","111","1110"],"isOfferRedeemed":true,"enableCta":true},{"partnerOfferId":"CRUNCHFITNESS-2","partnerName":"crunchfit","isStatic":false,"staticPromo":null,"partnerExternalUrl":"https://www.crunch.com/","title":"Crunchfitness FP","subTitle":"Crunchfitness only for Freshpass partners","contentPath":"/content/experience-fragments/xf/contenthub/external-partner-offer/crunchfitness/offer-detail/crunch-offer-2","bgImgPath":"https://images.albertsons-media.com/is/image/ABSStage/doordash-test-banner","bgImgAltText":"CrunchFitness","startDate":"2025-01-01 08:38:00.0","endDate":"2025-05-31 08:38:00.0","displayOrder":"2","partnerProgramType":"freshpass","paidSubscriptionSegment":["active-annual","active-monthly","trail-monthly","trail-annual"],"storeSegment":["division-socal"],"ctaText":"Claim Offer","ctaAltText":"Claim offer","analyticsTag":"Crunch Fitness Internal Page","ctaLandingPgTitleMobile":"Crunch Fitness","ctaTextMobile":"Claim offer","ctaAltTextMobile":"Click to save now for Crunch Fitness Promotions","storeList":["108","110","1105","1157","1165","1181","1319","132","1322"],"isOfferRedeemed":false,"enableCta":true}]};
        const successEmptyRespData = {
            "status": "SUCCESS",
            "message": "200",
            "errors": [
                {
                    "code": "140403",
                    "message": "No Partner Offers available during this time.",
                    "detail": "No Partner Offers available during this time."
                }
            ]
        };
        const errorRespData = {
            "status": "BAD_REQUEST",
            "message": "400",
            "errors": [
                {
                    "code": "140402",
                    "message": "Invalid/Expired Token.",
                    "detail": "Invalid/Expired Token."
                }
            ]
        };
        return of(successRespData);
    }


    getPromotionDetailContent(Contenturl) {
        let promotionDetailUrl = Contenturl + '.content.html?rand=' + Math.random();
        return this._http.get(promotionDetailUrl, {responseType: 'text'});
    }

    getPointForServicesOffers(currentEnvironment, userInfo) {
     const storeId = this.getStoreIdForUserType(userInfo);
     const headerOBJ = environment[currentEnvironment].rewardsLookupConfig.j4uHttpOptionsFilterAPIHeader;
            this.replaceHeaderBanner(headerOBJ);
            const options = {
                headers: {
                    ...headerOBJ,
                    'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                    'x-swy-correlation-id': uuidv4(),
                    'X-ABS-Client-ID': 'WEB'
                },
            };
             const reqBody = {
                         "offerPrototype": [
                            "REWARDS_ACCUMULATION"
                          ]
                    };
        let pointForServicesOffersUrl = this.getApplicationDomainURL() + environment[currentEnvironment].rewardsLookupConfig.getPointForServicesOffers;
        pointForServicesOffersUrl = pointForServicesOffersUrl + '?date=current&storeId=' + storeId;
        return this._http.post(pointForServicesOffersUrl,reqBody, options).pipe(map(data => {
            return data;
        }));
    }
}
